/**
 * Input source of an online battle (design D5) for the battle input seam (src/game/runtime/battle-input.ts): the
 * battle is played in lockstep through a LockstepCoordinator (src/session/lockstep.ts).
 *  - A tick of the local turn that was not recorded yet takes the `local` source (the live keyboard and interface, or a
 *    script in tests); the seam's recording of that tick (the effective frame) is relayed with
 *    coordinator.recordLocal.
 *  - Every other tick takes the frame the coordinator knows (the other participants' relayed input, the empty frames
 *    of a forced turn, or local input recorded before a rebuild). A tick whose input is not known yet must not be
 *    simulated: the game's loop asks `canAdvance(tick)` before it runs a battle frame and waits otherwise
 *    ("Хід суперника…"); next() throws for such a tick.
 *  - Every move start of the simulation goes to coordinator.onMoveStart after the tick's input was recorded (the owner
 *    ends its turn there, the controller of the next team reports the end of a forced turn), together with the state
 *    hash of the move start as the turn's checkpoint when the battle hashes checkpoints (session.hash).
 * The interface's battle entry points (weapon sheet, map clicks, scanner) are interactive only during the local turn.
 * The surrender button must not reach the seam online: a participant leaves with session.forfeit (the server refuses
 * a surrender frame in an online battle).
 *
 * Browser-safe: no node imports.
 */
import type { LockstepCoordinator } from '../../session/lockstep';
import type { InputFrame } from '../../session/input';
import type { BattleInputSource, SourceTick } from './battle-input';

export class LockstepInput implements BattleInputSource {
  readonly kind = 'lockstep';
  /** The turn clock of an online battle runs for everyone: the weapon sheet and the scanner do not pause the battle. */
  readonly freezesMenus = false;
  /** The tick the local source gave (to relay once the seam recorded it). */
  private localTick: number | null = null;
  private pendingMove: { tick: number; team: number; hash?: string } | null = null;
  /** Relay or check failures of the coordinator (tests, logs). */
  readonly errors: Error[] = [];

  constructor(
    readonly coordinator: LockstepCoordinator,
    private readonly local: BattleInputSource,
    private readonly opts: { checkpoints?: boolean } = {},
  ) {}

  get interactive(): boolean {
    return this.local.interactive && this.coordinator.isLocalTurn();
  }

  /** The tick is the local owner's to play now (its input comes from the local source). */
  private takesLocal(tick: number): boolean {
    const c = this.coordinator;
    return c.isLocalTurn() && tick > c.recordedTick && tick >= c.log.current.startTick;
  }

  /** The battle may run `tick` now: its input is local or already known. */
  canAdvance(tick: number): boolean {
    return this.takesLocal(tick) || this.coordinator.canAdvance(tick);
  }

  next(tick: number): SourceTick {
    if (this.takesLocal(tick)) {
      this.localTick = tick;
      return this.local.next(tick);
    }
    this.localTick = null;
    const f = this.coordinator.frameAt(tick);
    if (!f) throw new Error(`LockstepInput: the input of tick ${tick} is not known yet (ask canAdvance before running a battle frame)`);
    return { frame: f };
  }

  recorded(tick: number, frame: InputFrame): void {
    if (this.localTick === tick) {
      this.localTick = null;
      try {
        this.coordinator.recordLocal(tick, frame);
      } catch (err) {
        this.errors.push(err instanceof Error ? err : new Error(String(err)));
      }
    }
    const m = this.pendingMove;
    if (m && m.tick <= tick) {
      this.pendingMove = null;
      this.coordinator.onMoveStart(m.tick, m.team).catch((err) => this.errors.push(err instanceof Error ? err : new Error(String(err))));
      if (this.opts.checkpoints !== false && m.hash) {
        this.coordinator.checkpoint(m.tick, m.hash).catch((err) => this.errors.push(err instanceof Error ? err : new Error(String(err))));
      }
    }
  }

  moveStarted(tick: number, team: number, hash?: string): void {
    this.pendingMove = { tick, team, hash };
  }
}
